import { useState, useEffect, useRef, useCallback } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Swal from "sweetalert2";
import { useProducts } from "../../useHook";
import { getProductById } from "./useProductById";
import { sanitizePositionInput } from "../../../../Utils/positionInput";
import {
  DEALERSHIP_OPTIONS,
  UNIT_OPTIONS,
  urduFor,
} from "../../../../Utils/staticDropdownOptions";
import {
  productFormSchema,
  flattenProductFormErrors,
} from "../productFormSchema";
import { checkImageDimension } from "../utils/colorHelpers";
import {
  initialFormData,
  createEmptyColorRow,
} from "../utils/formDefaults";
import {
  mapProductSourceToFormData,
  createDefaultNewProductColors,
} from "../utils/mapProductSource";

export default function useProductForm({
  showModal,
  setShowModal,
  productToEdit,
  productId,
  addProduct,
  onSuccess,
}) {
  const { updateProduct, loading, error } = useProducts();

  const {
    register,
    control,
    handleSubmit: rhfHandleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    clearErrors,
    setError,
    formState,
  } = useForm({
    resolver: zodResolver(productFormSchema),
    defaultValues: initialFormData,
    mode: "onSubmit",
    reValidateMode: "onChange",
    criteriaMode: "all",
  });

  const { errors, isSubmitting, submitCount, isSubmitted } = formState;

  const { append, remove } = useFieldArray({
    control,
    name: "colors",
  });

  const formData = watch();

  const setFormData = useCallback(
    (updater) => {
      const prev = getValues();
      const next = typeof updater === "function" ? updater(prev) : updater;
      Object.keys(next).forEach((key) => {
        setValue(key, next[key], { shouldDirty: true, shouldValidate: false });
      });
    },
    [getValues, setValue],
  );

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (submitCount > 0 || isSubmitted) {
      setValidationErrors(flattenProductFormErrors(errors));
    }
  }, [errors, submitCount, isSubmitted]);

  const clearFieldErrors = useCallback(
    (...names) => {
      if (names.length === 0) {
        clearErrors();
        setValidationErrors({});
        return;
      }
      clearErrors(...names);
      setValidationErrors((prev) => {
        const next = { ...prev };
        names.flat().forEach((name) => {
          if (name === "categoryIds") delete next.categories;
          if (name === "description") delete next.description;
          if (name === "colors") {
            Object.keys(next).forEach((key) => {
              if (key === "variants" || key.startsWith("variant_")) {
                delete next[key];
              }
            });
          }
          if (name === "name") delete next.name;
          if (name === "currency") delete next.currency;
          const variantMatch = String(name).match(
            /^colors\.(\d+)\.(name|color|color_or_image|variant_image|images)$/,
          );
          if (variantMatch) {
            const [, idx, field] = variantMatch;
            delete next[
              `variant_${idx}_${field === "variant_image" ? "variant_image" : field}`
            ];
            if (field === "color") {
              delete next[`variant_${idx}_color_or_image`];
              delete next[`variant_${idx}_variant_image`];
            }
            if (field === "images") delete next[`variant_${idx}_images`];
          }
        });
        return next;
      });
    },
    [clearErrors],
  );

  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  const fileInputRefs = useRef([]);
  const variantImageInputRefs = useRef([]);
  const textureImageInputRefs = useRef([]);
  const variantNameInputRefs = useRef({});
  const hasLoadedData = useRef(false);
  const loadedProductIdRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  useEffect(() => {
    if (!showModal) {
      reset(initialFormData);
      clearFieldErrors();
      setValidationErrors({});
      hasLoadedData.current = false;
      loadedProductIdRef.current = null;
      return;
    }

    // Prevent GET loop when callback identities (getProductById/reset/etc) change
    if (productId && loadedProductIdRef.current === String(productId)) {
      return;
    }
    if (!productId && !productToEdit && hasLoadedData.current) {
      return;
    }

    let cancelled = false;

    const loadProduct = async () => {
      try {
        if (productId && !productToEdit) {
          loadedProductIdRef.current = String(productId);
          const dataFromApi = await getProductById(productId);
          if (cancelled) return;
          if (dataFromApi) {
            reset(mapProductSourceToFormData(dataFromApi));
            hasLoadedData.current = true;
          }
        } else if (productToEdit) {
          loadedProductIdRef.current = String(productToEdit.id || "edit");
          if (cancelled) return;
          reset(mapProductSourceToFormData(productToEdit));
          hasLoadedData.current = true;
        } else if (!productToEdit && !productId && !hasLoadedData.current) {
          if (cancelled) return;
          setFormData((prev) => {
            if (prev.colors.length === 0) {
              return {
                ...prev,
                colors: createDefaultNewProductColors(),
              };
            }
            return prev;
          });
          hasLoadedData.current = true;
          loadedProductIdRef.current = "new";
        }
      } catch (loadError) {
        console.error("Error loading product:", loadError);
        loadedProductIdRef.current = null;
      }
    };

    loadProduct();

    return () => {
      cancelled = true;
    };
    // Intentionally only product identity / modal visibility — avoids fetch loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, productId]);

  const handleClose = () => {
    reset(initialFormData);
    clearFieldErrors();
    setValidationErrors({});
    setIsCategoryDropdownOpen(false);
    hasLoadedData.current = false;
    loadedProductIdRef.current = null;
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "dealership") {
        next.dealership_ur = urduFor(DEALERSHIP_OPTIONS, value);
      }
      if (name === "unit") {
        next.unit_ur = urduFor(UNIT_OPTIONS, value);
      }
      return next;
    });
  };

  const toggleCategory = (categoryId) => {
    setFormData((prev) => {
      const isSelected = prev.categoryIds.includes(categoryId);
      if (isSelected) {
        return {
          ...prev,
          categoryIds: prev.categoryIds.filter((id) => id !== categoryId),
        };
      }
      return {
        ...prev,
        categoryIds: [...prev.categoryIds, categoryId],
      };
    });
    clearFieldErrors("categoryIds");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setIsCategoryDropdownOpen(false);
      }
    };

    if (isCategoryDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCategoryDropdownOpen]);

  const handleVariantMoqChange = (index, value) => {
    if (value === "" || (Number(value) >= 1 && Number(value) <= 10000)) {
      handleVariantChange(index, "moq", value);
    }
  };

  const handleVariantChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      updatedColors[index] = {
        ...updatedColors[index],
        [field]: value,
      };
      return { ...prev, colors: updatedColors };
    });
  };

  const handleAddImageClick = (index) => {
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].click();
    }
  };

  const handleColorImageChange = async (colorIndex, files) => {
    if (!files || files.length === 0) return;

    const validatedFiles = [];
    for (const file of Array.from(files)) {
      try {
        const isValid = await checkImageDimension(file);
        if (isValid) {
          validatedFiles.push(file);
        } else {
          alert(
            `Image "${file.name}" must be exactly 450x450. This file was skipped.`,
          );
        }
      } catch (err) {
        console.error("Error loading file:", file.name, err);
      }
    }

    if (validatedFiles.length > 0) {
      setFormData((prev) => {
        const updatedColors = [...prev.colors];
        const existingNewFiles = updatedColors[colorIndex].newImageFiles || [];
        const totalImages =
          (updatedColors[colorIndex].existingImages?.length || 0) +
          existingNewFiles.length +
          validatedFiles.length;

        if (totalImages > 9999) {
          alert(
            `Maximum 9999 images per variant allowed. Only ${9999 - (updatedColors[colorIndex].existingImages?.length || 0) - existingNewFiles.length} more image(s) can be added.`,
          );
          return prev;
        }

        updatedColors[colorIndex].newImageFiles = [
          ...existingNewFiles,
          ...validatedFiles,
        ];
        const existingPositions =
          updatedColors[colorIndex].newImagePositions || [];
        const indexedExisting = (
          updatedColors[colorIndex].existingImages || []
        ).filter(
          (img) =>
            !(typeof img === "object" && img.isPrimary) &&
            !(typeof img === "object" && Number(img.position) === 0),
        );
        const maxPos = Math.max(
          0,
          ...indexedExisting.map((img, i) =>
            typeof img === "object" &&
            img.position != null &&
            Number(img.position) >= 1
              ? Number(img.position)
              : i + 1,
          ),
          ...existingPositions.map((p) => {
            const n = Number(p);
            return Number.isInteger(n) && n >= 1 ? n : 0;
          }),
        );
        updatedColors[colorIndex].newImagePositions = [
          ...existingPositions,
          ...validatedFiles.map((_, i) => maxPos + i + 1),
        ];
        updatedColors[colorIndex].newImageAlts = [
          ...(updatedColors[colorIndex].newImageAlts || []),
          ...validatedFiles.map(() => ""),
        ];
        updatedColors[colorIndex].newImageTitles = [
          ...(updatedColors[colorIndex].newImageTitles || []),
          ...validatedFiles.map(() => ""),
        ];
        return { ...prev, colors: updatedColors };
      });

      clearFieldErrors(`colors.${colorIndex}.images`);
    }
  };

  const handleRemoveColorImage = (colorIndex, imageIndex, isExisting) => {
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      const color = { ...updatedColors[colorIndex] };
      if (isExisting) {
        color.existingImages = (color.existingImages || []).filter(
          (_, i) => i !== imageIndex,
        );
      } else {
        color.newImageFiles = (color.newImageFiles || []).filter(
          (_, i) => i !== imageIndex,
        );
        color.newImagePositions = (color.newImagePositions || []).filter(
          (_, i) => i !== imageIndex,
        );
        color.newImageAlts = (color.newImageAlts || []).filter(
          (_, i) => i !== imageIndex,
        );
        color.newImageTitles = (color.newImageTitles || []).filter(
          (_, i) => i !== imageIndex,
        );
        const currentPrimary = color.newImagePrimaryIndex;
        if (currentPrimary == null) {
          // no-op
        } else if (currentPrimary === imageIndex) {
          color.newImagePrimaryIndex = null;
        } else if (currentPrimary > imageIndex) {
          color.newImagePrimaryIndex = currentPrimary - 1;
        }
      }
      updatedColors[colorIndex] = color;
      return { ...prev, colors: updatedColors };
    });
  };

  const handleSetPrimaryColorImage = (colorIndex, imageIndex, isExisting) => {
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      const color = { ...updatedColors[colorIndex] };
      const existingImages = [...(color.existingImages || [])];
      const newPositions = [...(color.newImagePositions || [])];

      if (isExisting) {
        let pos = 1;
        color.existingImages = existingImages.map((img, i) => {
          const asObj =
            typeof img === "string"
              ? { url: img, isPrimary: false, position: i + 1 }
              : { ...img };
          if (i === imageIndex) {
            return { ...asObj, isPrimary: true, position: "" };
          }
          return { ...asObj, isPrimary: false, position: pos++ };
        });
        color.newImagePrimaryIndex = null;
        color.newImagePositions = newPositions.map((p, i) => {
          const n = Number(p);
          return Number.isInteger(n) && n >= 1 ? n : i + 1;
        });
      } else {
        let pos = 1;
        color.existingImages = existingImages.map((img, i) => {
          const asObj =
            typeof img === "string"
              ? { url: img, isPrimary: false, position: i + 1 }
              : { ...img, isPrimary: false };
          return { ...asObj, isPrimary: false, position: pos++ };
        });
        color.newImagePrimaryIndex = imageIndex;
        color.newImagePositions = newPositions.map((p, i) =>
          i === imageIndex
            ? ""
            : p != null && Number(p) >= 1
              ? Number(p)
              : i + 1,
        );
      }

      updatedColors[colorIndex] = color;
      return { ...prev, colors: updatedColors };
    });
  };

  const onExistingColorImageSeoChange = (colorIndex, imageIndex, field, value) => {
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      const images = [...(updatedColors[colorIndex].existingImages || [])];
      if (images[imageIndex]) {
        images[imageIndex] = { ...images[imageIndex], [field]: value };
        updatedColors[colorIndex].existingImages = images;
      }
      return { ...prev, colors: updatedColors };
    });
  };

  const onNewColorImageSeoChange = (colorIndex, imageIndex, field, value) => {
    const key = field === "image_alt" ? "newImageAlts" : "newImageTitles";
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      const next = [...(updatedColors[colorIndex][key] || [])];
      next[imageIndex] = value;
      updatedColors[colorIndex][key] = next;
      return { ...prev, colors: updatedColors };
    });
  };

  const setVariantPosition = (variantName, value) => {
    const position = sanitizePositionInput(value);
    setFormData((prev) => ({
      ...prev,
      colors: prev.colors.map((c) =>
        c.name === variantName ? { ...c, position } : c,
      ),
    }));
  };

  const onExistingColorImagePositionChange = (
    colorIndex,
    imageIndex,
    value,
  ) => {
    const cleaned = sanitizePositionInput(value);
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      const color = updatedColors[colorIndex];
      const images = [...(color.existingImages || [])];
      const current = images[imageIndex];
      if (typeof current === "object" && current?.isPrimary) {
        return prev;
      }
      images[imageIndex] =
        typeof current === "string"
          ? { url: current, position: cleaned, isPrimary: false }
          : { ...current, position: cleaned };
      updatedColors[colorIndex] = { ...color, existingImages: images };
      return { ...prev, colors: updatedColors };
    });
  };

  const onNewColorImagePositionChange = (colorIndex, imageIndex, value) => {
    const cleaned = sanitizePositionInput(value);
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      const color = updatedColors[colorIndex];
      const positions = [...(color.newImagePositions || [])];
      positions[imageIndex] = cleaned;
      updatedColors[colorIndex] = { ...color, newImagePositions: positions };
      return { ...prev, colors: updatedColors };
    });
  };

  const handleVariantImageChange = async (colorIndex, file) => {
    if (!file) return;

    try {
      const isValid = await checkImageDimension(file);
      if (!isValid) {
        alert(`Variant image "${file.name}" must be exactly 450x450.`);
        return;
      }

      setFormData((prev) => {
        const updatedColors = [...prev.colors];
        updatedColors[colorIndex] = {
          ...updatedColors[colorIndex],
          variantImageFile: file,
        };
        return { ...prev, colors: updatedColors };
      });

      clearFieldErrors([
        `colors.${colorIndex}.variant_image`,
        `colors.${colorIndex}.color_or_image`,
        `colors.${colorIndex}.color`,
      ]);
    } catch (err) {
      console.error("Error loading variant image:", file.name, err);
      alert(`Error loading variant image: ${file.name}`);
    }
  };

  const handleRemoveVariantImage = (colorIndex) => {
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      const variant = updatedColors[colorIndex];
      const hasColor = variant.color && variant.color.trim() !== "";

      updatedColors[colorIndex] = {
        ...updatedColors[colorIndex],
        variantImageFile: null,
        existingVariantImage: null,
      };

      if (!hasColor) {
        setTimeout(() => {
          setError(`colors.${colorIndex}.variant_image`, {
            type: "manual",
            message: "Variant image is required when color is not provided",
          });
          setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [`variant_${colorIndex}_variant_image`]:
              "Variant image is required when color is not provided",
          }));
        }, 0);
      }

      return { ...prev, colors: updatedColors };
    });
  };

  const handleTextureImageChange = (colorIndex, file) => {
    if (!file) return;
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      updatedColors[colorIndex] = {
        ...updatedColors[colorIndex],
        textureImageFile: file,
        texture: true,
      };
      return { ...prev, colors: updatedColors };
    });
  };

  const handleAddTextureImageClick = (colorIndex) => {
    if (textureImageInputRefs.current[colorIndex]) {
      textureImageInputRefs.current[colorIndex].click();
    }
  };

  const handleRemoveTextureImage = (colorIndex) => {
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      updatedColors[colorIndex] = {
        ...updatedColors[colorIndex],
        textureImageFile: null,
        existingTextureImage: null,
        texture: false,
      };
      return { ...prev, colors: updatedColors };
    });
  };

  const groupColorsByVariant = (colors) => {
    if (formData.singleVarinet) {
      return {
        "": colors.map((color, index) => ({ ...color, originalIndex: index })),
      };
    }
    const grouped = {};
    colors.forEach((color, index) => {
      const variantName = color.name || "";
      if (!grouped[variantName]) {
        grouped[variantName] = [];
      }
      grouped[variantName].push({ ...color, originalIndex: index });
    });
    return grouped;
  };

  const addColor = (variantName = null) => {
    const totalColors = getValues("colors")?.length || 0;
    if (totalColors >= 9999) {
      alert(`Maximum 9999 color entries allowed across all variants`);
      return;
    }

    const colors = getValues("colors") || [];
    const singleVarinet = getValues("singleVarinet");
    const inheritedSingleName =
      singleVarinet && colors[0]?.name ? colors[0].name : "";

    append(createEmptyColorRow({ name: variantName || inheritedSingleName || "" }));
    clearFieldErrors("colors");
  };

  const addVariant = () => {
    const groupedVariants = groupColorsByVariant(getValues("colors") || []);
    const variantCount = Object.keys(groupedVariants).length;

    if (variantCount >= 9999) {
      alert("Maximum 9999 variants allowed");
      return;
    }
    const tempVariantId = `__NEW_VARIANT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    addColor(tempVariantId);
  };

  const addColorToVariant = (variantName) => {
    addColor(variantName);
  };

  const removeColor = (index) => {
    remove(index);
  };

  const removeVariantGroup = (colorsInVariant) => {
    const indicesToRemove = colorsInVariant
      .map((c) => c.originalIndex)
      .sort((a, b) => b - a);
    setFormData((prev) => {
      let updatedColors = [...prev.colors];
      indicesToRemove.forEach((idx) => {
        updatedColors = updatedColors.filter((_, i) => i !== idx);
      });
      return { ...prev, colors: updatedColors };
    });
  };

  const onInvalidSubmit = (formErrors) => {
    const flat = flattenProductFormErrors(formErrors);
    setValidationErrors(flat);

    const scrollTargets = [
      ["categories", '[data-field="categories"]'],
      ["name", '[data-field="name"]'],
      ["description", '[data-field="description"]'],
      ["variants", '[data-field="variants"]'],
    ];

    for (const [key, selector] of scrollTargets) {
      if (flat[key]) {
        const el = document.querySelector(selector);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
      }
    }

    const firstVariantKey = Object.keys(flat).find((k) =>
      k.startsWith("variant_"),
    );
    if (firstVariantKey) {
      const variantIndex = parseInt(firstVariantKey.split("_")[1], 10);
      const variantElement = document.querySelector(
        `[data-variant-index="${variantIndex}"]`,
      );
      if (variantElement) {
        variantElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
      const variantsSection = document.querySelector('[data-field="variants"]');
      if (variantsSection) {
        variantsSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const onValidSubmit = async (values) => {
    setValidationErrors({});
    const submitFormData = values;
    const data = new FormData();

    Object.keys(submitFormData).forEach((key) => {
      if (key === "colors" || key === "video") {
        return;
      }
      if (key === "categoryIds") {
        if (submitFormData.categoryIds && submitFormData.categoryIds.length > 0) {
          data.append("categoryIds", JSON.stringify(submitFormData.categoryIds));
        }
        return;
      }
      if (key === "status") {
        if (productId || productToEdit) {
          data.append(key, submitFormData[key]);
        } else {
          data.append(key, "true");
        }
        return;
      }
      if (key === "singleVarinet") {
        data.append(key, submitFormData[key] ? "true" : "false");
        return;
      }
      if (key === "existingTechnicalDataSheet") {
        return;
      }
      if (key === "currency") {
        const currencyValue =
          submitFormData.currency && String(submitFormData.currency).trim() !== ""
            ? submitFormData.currency
            : "PKR";
        data.append(key, currencyValue);
        return;
      }
      if (
        submitFormData.singleVarinet &&
        (key === "weight" ||
          key === "unit" ||
          key === "unit_ur" ||
          key === "price" ||
          key === "dimensions" ||
          key === "moq")
      ) {
        if (
          submitFormData[key] !== "" &&
          submitFormData[key] !== null &&
          submitFormData[key] !== undefined
        ) {
          data.append(key, submitFormData[key]);
        }
        return;
      }
      if (
        submitFormData[key] !== "" &&
        submitFormData[key] !== null &&
        submitFormData[key] !== undefined
      ) {
        data.append(key, submitFormData[key]);
      }
    });

    if (submitFormData.video) {
      data.append("video", submitFormData.video);
    }

    if (submitFormData.colors && submitFormData.colors.length > 0) {
      const variantsData = submitFormData.colors.map((variant, index) => {
        const singleVariantName =
          submitFormData.colors[0]?.name && String(submitFormData.colors[0].name).trim()
            ? String(submitFormData.colors[0].name).trim()
            : null;
        const singleVariantNameUr =
          submitFormData.colors[0]?.name_ur && String(submitFormData.colors[0].name_ur).trim()
            ? String(submitFormData.colors[0].name_ur).trim()
            : null;
        const variantObj = {
          name: submitFormData.singleVarinet
            ? singleVariantName
            : variant.name || null,
          name_ur: submitFormData.singleVarinet
            ? singleVariantNameUr
            : variant.name_ur || null,
          variantName_ur: submitFormData.singleVarinet
            ? singleVariantNameUr
            : variant.name_ur || null,
          color: variant.color || null,
          color_ur: variant.color_ur || null,
          texture: variant.texture === true,
          texture_code: variant.textureCode || null,
        };

        if (submitFormData.singleVarinet) {
          if (index === 0) {
            variantObj.price = submitFormData.price
              ? parseFloat(submitFormData.price)
              : variant.price
                ? parseFloat(variant.price)
                : null;
            variantObj.weight = submitFormData.weight
              ? parseFloat(submitFormData.weight)
              : variant.weight
                ? parseFloat(variant.weight)
                : null;
            variantObj.dimension =
              submitFormData.dimensions || variant.dimension || null;
            variantObj.unit = submitFormData.unit || variant.unit || null;
            variantObj.unit_ur = submitFormData.unit_ur || variant.unit_ur || null;
            variantObj.moq = submitFormData.moq
              ? parseInt(submitFormData.moq)
              : variant.moq
                ? parseInt(variant.moq)
                : null;
          }
        } else {
          variantObj.price = variant.price ? parseFloat(variant.price) : null;
          variantObj.weight = variant.weight
            ? parseFloat(variant.weight)
            : null;
          variantObj.dimension = variant.dimension || null;
          variantObj.unit = variant.unit || null;
          variantObj.unit_ur = variant.unit_ur || null;
          variantObj.moq = variant.moq ? parseInt(variant.moq) : null;
        }

        if (productId || productToEdit) {
          if (variant.id) {
            variantObj.id = variant.id;
          }
          variantObj.images = (variant.existingImages || []).map((img, imgIndex) => {
            if (typeof img === "string") {
              return { url: img, position: imgIndex + 1, isPrimary: false };
            }
            const isPrimary = !!img.isPrimary;
            return {
              url: img.url || img.imageUrl || "",
              isPrimary,
              position: isPrimary
                ? 0
                : img.position != null && Number(img.position) >= 1
                  ? Number(img.position)
                  : imgIndex + 1,
              id: img.id || null,
              image_alt: img.image_alt || "",
              image_title: img.image_title || "",
            };
          });
          if (variant.existingVariantImage && !variant.variantImageFile) {
            variantObj.variant_image = variant.existingVariantImage;
          }
          if (
            variant.texture &&
            variant.existingTextureImage &&
            !variant.textureImageFile
          ) {
            variantObj.texture_image = variant.existingTextureImage;
          }
        }

        variantObj.variantImageAlt = variant.variantImageAlt || null;
        variantObj.variantImageTitle = variant.variantImageTitle || null;
        variantObj.textureImageAlt = variant.textureImageAlt || null;
        variantObj.textureImageTitle = variant.textureImageTitle || null;

        const newImageAlts = variant.newImageAlts || [];
        const newImageTitles = variant.newImageTitles || [];
        if ((variant.newImageFiles || []).length > 0) {
          variantObj.imageAlts = newImageAlts;
          variantObj.imageTitles = newImageTitles;
        }

        if (variant.position !== "" && variant.position != null) {
          const parsedPosition = parseInt(variant.position, 10);
          if (Number.isInteger(parsedPosition) && parsedPosition >= 1) {
            variantObj.position = parsedPosition;
          } else if (!submitFormData.singleVarinet) {
            const grouped = groupColorsByVariant(submitFormData.colors);
            const names = Object.keys(grouped);
            const groupIndex = names.indexOf(variant.name);
            variantObj.position = groupIndex >= 0 ? groupIndex + 1 : index + 1;
          } else {
            variantObj.position = 1;
          }
        } else if (!submitFormData.singleVarinet) {
          const grouped = groupColorsByVariant(submitFormData.colors);
          const names = Object.keys(grouped);
          const groupIndex = names.indexOf(variant.name);
          variantObj.position = groupIndex >= 0 ? groupIndex + 1 : index + 1;
        } else {
          variantObj.position = 1;
        }

        const existingImagePositions = (variant.existingImages || []).map(
          (img, i) => {
            if (typeof img === "object" && img.isPrimary) return "";
            if (typeof img === "object" && img.position != null && Number(img.position) >= 1) {
              return img.position;
            }
            return i + 1;
          },
        );
        const newPositions = (variant.newImagePositions || []).map((p, i) => {
          if (variant.newImagePrimaryIndex === i) return "";
          return p;
        });
        if (existingImagePositions.length > 0 || newPositions.length > 0) {
          variantObj.imagePositions = [
            ...existingImagePositions,
            ...newPositions,
          ];
        }

        if (
          variant.newImagePrimaryIndex != null &&
          Number.isInteger(Number(variant.newImagePrimaryIndex))
        ) {
          variantObj.primaryNewImageIndex = Number(variant.newImagePrimaryIndex);
        }

        return variantObj;
      });

      data.append("colors", JSON.stringify(variantsData));

      submitFormData.colors.forEach((variant, index) => {
        if (variant.variantImageFile) {
          data.append(
            `colors[${index}][variant_image]`,
            variant.variantImageFile,
          );
        }
      });

      submitFormData.colors.forEach((variant, index) => {
        if (variant.newImageFiles && variant.newImageFiles.length > 0) {
          variant.newImageFiles.forEach((imageFile) => {
            data.append(`colors[${index}][images]`, imageFile);
          });
        }
      });

      submitFormData.colors.forEach((variant, index) => {
        if (variant.textureImageFile) {
          data.append(
            `colors[${index}][texture_image]`,
            variant.textureImageFile,
          );
        }
      });
    } else {
      data.append("colors", JSON.stringify([]));
    }

    try {
      if (productId || productToEdit) {
        const updateId = productId || productToEdit.id;
        await updateProduct(updateId, data);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Product updated successfully",
          showConfirmButton: true,
          timer: 2000,
          timerProgressBar: true,
        });
        if (typeof onSuccess === "function") {
          onSuccess();
        }
        handleClose();
      } else {
        await addProduct(data);
        if (typeof onSuccess === "function") {
          onSuccess();
        }
        handleClose();
      }
    } catch (err) {
      console.error("Error submitting product:", err);
      alert(
        "Error submitting product: " +
          (err?.response?.data?.message || err.message || "Unknown error"),
      );
    }
  };

  const handleSubmit = rhfHandleSubmit(onValidSubmit, onInvalidSubmit);

  return {
    register,
    control,
    formData,
    setFormData,
    validationErrors,
    isCategoryDropdownOpen,
    setIsCategoryDropdownOpen,
    categoryDropdownRef,
    fileInputRefs,
    variantImageInputRefs,
    textureImageInputRefs,
    variantNameInputRefs,
    handleClose,
    handleChange,
    toggleCategory,
    handleVariantChange,
    handleVariantMoqChange,
    handleAddImageClick,
    handleColorImageChange,
    handleRemoveColorImage,
    handleSetPrimaryColorImage,
    setVariantPosition,
    onExistingColorImagePositionChange,
    onNewColorImagePositionChange,
    onExistingColorImageSeoChange,
    onNewColorImageSeoChange,
    handleVariantImageChange,
    handleRemoveVariantImage,
    handleTextureImageChange,
    handleAddTextureImageClick,
    handleRemoveTextureImage,
    groupColorsByVariant,
    addColor,
    addVariant,
    addColorToVariant,
    removeColor,
    removeVariantGroup,
    clearFieldErrors,
    handleSubmit,
    productId,
    productToEdit,
    loading,
    error,
    isSubmitting,
  };
}
